/**
 * 制作工作台主页 · 路由 /director/$episodeId
 *
 * 守卫顺序:无 episodeId → 提示 → 项目大厅 → 未完成编剧 → 编剧工作台
 *           → 未完成 director 初始化 → DirectorWorkflowDialog
 *           → 已完成 → 工作区(CastPanel + EpisodeSceneNav + Shot 列表)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Clapperboard, Layers, Loader2, Wand2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { DirectorWorkflowDialog } from './DirectorWorkflowDialog';
import { CastPanel } from './CastPanel';
import { EpisodeSceneNav } from './EpisodeSceneNav';
import { SceneScriptCard } from './SceneScriptCard';
import { SceneVideoGachaPanel } from './SceneVideoGachaPanel';
import { SceneNotesField } from './SceneNotesField';
import { CameraPromptField } from './CameraPromptField';
import { SceneStoryboardPanel } from './SceneStoryboardPanel';
import type { DirectorPlan } from './types';

interface DirectorStatus {
  directorInitializedAt: string | null;
  directorInitializingAt: string | null;
  writerCompletedAt: string | null;
  episodeCount: number | null;
  plan: unknown;
  running: boolean;
}

export function DirectorPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { episodeId?: string };
  const episodeId = params.episodeId;

  const [resumeOpen, setResumeOpen] = useState(false);

  const statusQuery = useQuery<DirectorStatus>({
    queryKey: ['director', 'status', episodeId],
    queryFn: () =>
      api.get(`director/episodes/${episodeId}/director/status`).json<DirectorStatus>(),
    enabled: Boolean(episodeId),
    retry: false,
  });

  const directorDone = Boolean(statusQuery.data?.directorInitializedAt);

  const planQuery = useQuery<DirectorPlan>({
    queryKey: ['director', 'plan', episodeId],
    queryFn: () =>
      api.get(`director/episodes/${episodeId}/director/plan`).json<DirectorPlan>(),
    enabled: Boolean(episodeId) && directorDone,
  });

  if (!episodeId) {
    return (
      <Empty
        msg="还没有打开剧集 · 请到项目大厅选择一个完成编剧的项目进行制作"
        action={
          <Button onClick={() => navigate({ to: '/projects' })}>
            <Layers className="h-4 w-4" /> 前往项目大厅
          </Button>
        }
      />
    );
  }

  if (statusQuery.isLoading) {
    return <Loading msg="正在检查制作台状态…" />;
  }

  const status = statusQuery.data;
  const writerDone = Boolean(status?.writerCompletedAt);

  // 守卫顺序:directorDone=true → 直接进工作区(已初始化的剧集即便 writerCompletedAt 缺失也能进);
  // directorDone=false 且 writerDone=false → 跳编剧;
  // directorDone=false 且 writerDone=true → DirectorWorkflowDialog
  if (!directorDone && !writerDone) {
    return (
      <Empty
        msg="该剧集尚未完成编剧 · 请先回到编剧工作台完成分集脚本生成"
        action={
          <div className="flex gap-2">
            <Button
              variant="agent"
              agentColor="writer"
              onClick={() =>
                navigate({
                  to: '/writer/$episodeId' as never,
                  params: { episodeId } as never,
                })
              }
            >
              <Clapperboard className="h-4 w-4" /> 去编剧工作台
            </Button>
            <Button variant="secondary" onClick={() => navigate({ to: '/projects' })}>
              <Layers className="h-4 w-4" /> 项目大厅
            </Button>
          </div>
        }
      />
    );
  }

  if (!directorDone) {
    return (
      <>
        <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3 p-6 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
          <p className="text-sm text-neutral-700">
            制作台尚未完成初始化 · 正在生成人物卡 / 场地卡 / 导入场景数据
          </p>
          <div className="flex gap-2">
            <Button variant="agent" agentColor="director" onClick={() => setResumeOpen(true)}>
              <Wand2 className="h-4 w-4" /> 查看初始化进度
            </Button>
            <Button variant="secondary" onClick={() => navigate({ to: '/projects' })}>
              <Layers className="h-4 w-4" /> 返回项目大厅
            </Button>
          </div>
        </div>
        <DirectorWorkflowDialog
          open={resumeOpen}
          onOpenChange={setResumeOpen}
          episodeId={episodeId}
          onFinish={() => statusQuery.refetch()}
          onEnterDirector={() => {
            setResumeOpen(false);
            statusQuery.refetch();
          }}
        />
      </>
    );
  }

  if (planQuery.isLoading) {
    return <Loading msg="正在载入制作台数据…" />;
  }

  if (planQuery.error || !planQuery.data) {
    return (
      <Empty
        msg="制作台数据加载失败 · 请刷新或返回项目大厅"
        action={
          <Button onClick={() => planQuery.refetch()}>
            <Loader2 className="h-4 w-4" /> 重新加载
          </Button>
        }
      />
    );
  }

  return <Workspace episodeId={episodeId} plan={planQuery.data} />;
}

function Workspace({ episodeId, plan }: { episodeId: string; plan: DirectorPlan }) {
  const navigate = useNavigate();
  const sortedEpisodes = useMemo(
    () => [...plan.episodes].sort((a, b) => a.episodeIndex - b.episodeIndex),
    [plan.episodes],
  );

  const [episodeIndex, setEpisodeIndex] = useState<number>(
    sortedEpisodes[0]?.episodeIndex ?? 1,
  );
  const [sceneId, setSceneId] = useState<string | null>(null);
  const [usePrevEndingFrameByScene, setUsePrevEndingFrameByScene] = useState<Record<string, boolean>>({});

  const currentEpisode = useMemo(
    () => sortedEpisodes.find((e) => e.episodeIndex === episodeIndex),
    [sortedEpisodes, episodeIndex],
  );

  useEffect(() => {
    if (!currentEpisode) return;
    if (!sceneId || !currentEpisode.scenes.some((s) => s.sceneId === sceneId)) {
      setSceneId(currentEpisode.scenes[0]?.sceneId ?? null);
    }
  }, [currentEpisode, sceneId]);

  const currentScene = useMemo(
    () => currentEpisode?.scenes.find((s) => s.sceneId === sceneId) ?? null,
    [currentEpisode, sceneId],
  );

  const currentLocation = useMemo(() => {
    if (!currentScene?.locationId) return null;
    return plan.locations.find((l) => l.id === currentScene.locationId) ?? null;
  }, [plan.locations, currentScene]);

  const prevStoryboardFrameUrl = useMemo(() => {
    if (!currentEpisode || !currentScene) return null;
    const scenes = currentEpisode.scenes ?? [];
    const idx = scenes.findIndex((s) => s.sceneId === currentScene.sceneId);
    if (idx <= 0) return null;
    const prevScene = scenes[idx - 1];
    return prevScene?.endingContinuityFrameUrl ?? prevScene?.endingFrameUrl ?? prevScene?.endingRawFrameUrl ?? null;
  }, [currentEpisode, currentScene]);

  const prevVideoFrameUrl = useMemo(() => {
    if (!currentEpisode || !currentScene) return null;
    const scenes = currentEpisode.scenes ?? [];
    const idx = scenes.findIndex((s) => s.sceneId === currentScene.sceneId);
    if (idx <= 0) return null;
    const prevScene = scenes[idx - 1];
    return prevScene?.endingContinuityFrameUrl ?? prevScene?.endingFrameUrl ?? prevScene?.endingRawFrameUrl ?? null;
  }, [currentEpisode, currentScene]);

  const hasPrevEndingFrame = Boolean(prevStoryboardFrameUrl || prevVideoFrameUrl);
  const usePrevEndingFrame = currentScene && hasPrevEndingFrame
    ? usePrevEndingFrameByScene[currentScene.sceneId] ?? false
    : false;
  const setUsePrevEndingFrame = (value: boolean | ((prev: boolean) => boolean)) => {
    if (!currentScene || !hasPrevEndingFrame) return;
    setUsePrevEndingFrameByScene((prevMap) => {
      const prevValue = prevMap[currentScene.sceneId] ?? false;
      const nextValue = typeof value === 'function' ? value(prevValue) : value;
      return { ...prevMap, [currentScene.sceneId]: nextValue };
    });
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      <CastPanel
        episodeId={episodeId}
        characters={plan.characters}
        npcs={plan.npcs}
        locations={plan.locations}
        props={plan.props}
        production={plan.production ?? null}
        episodes={plan.episodes}
      />

      <section className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-outline-variant/30 bg-surface px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-neutral-800">
            <Clapperboard className="h-4 w-4 shrink-0 text-brand-600" />
            <span className="truncate">制作工作台</span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              navigate({
                to: '/writer/$episodeId' as never,
                params: { episodeId } as never,
              })
            }
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回编剧工作台
          </Button>
        </div>
        <EpisodeSceneNav
          episodes={sortedEpisodes}
          episodeIndex={episodeIndex}
          sceneId={sceneId}
          onEpisodeChange={(idx) => {
            setEpisodeIndex(idx);
            setSceneId(null);
          }}
          onSceneChange={setSceneId}
        />

        <div className="flex-1 overflow-y-auto bg-surface-variant/20 px-5 py-4">
          {!currentScene ? (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              该集暂无场景
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-4">
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-semibold tracking-tight">
                  {currentScene.sceneId}
                  {currentLocation && (
                    <span className="ml-2 text-sm font-normal text-neutral-500">
                      · {currentLocation.name}
                    </span>
                  )}
                </h2>
              </header>

              <SceneScriptCard scene={currentScene} />

              <SceneNotesField
                episodeId={episodeId}
                episodeIndex={episodeIndex}
                sceneId={currentScene.sceneId}
                initialNotes={currentScene.moreNotes ?? ''}
              />

              <CameraPromptField
                episodeId={episodeId}
                episodeIndex={episodeIndex}
                sceneId={currentScene.sceneId}
                initialPrompt={currentScene.cameraPrompt ?? ''}
                initialMentions={currentScene.mentionRefs ?? []}
                characters={plan.characters}
                npcs={plan.npcs}
                locations={plan.locations}
                props={plan.props}
                prevEndingFrameUrl={prevVideoFrameUrl}
                usePrevEndingFrame={usePrevEndingFrame}
                onUsePrevEndingFrameChange={setUsePrevEndingFrame}
              />

              <SceneStoryboardPanel
                key={`sb-${currentScene.sceneId}`}
                episodeId={episodeId}
                episodeIndex={episodeIndex}
                sceneId={currentScene.sceneId}
                scene={currentScene}
                aspectRatio={plan.production?.aspectRatio ?? '9:16'}
                prevEndingFrameUrl={prevStoryboardFrameUrl}
                usePrevEndingFrame={usePrevEndingFrame}
              />

              <SceneVideoGachaPanel
                key={currentScene.sceneId}
                episodeId={episodeId}
                episodeIndex={episodeIndex}
                sceneId={currentScene.sceneId}
                scene={currentScene}
                usePrevEndingFrame={usePrevEndingFrame}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Loading({ msg }: { msg: string }) {
  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3 p-6 text-center text-sm text-neutral-500">
      <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
      {msg}
    </div>
  );
}

function Empty({ msg, action }: { msg: string; action?: React.ReactNode }) {
  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-sm text-neutral-600">{msg}</p>
      {action}
    </div>
  );
}
